<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { InputRow, Select } from '@/components/ui/form';
    import { Label } from '@/components/ui/label';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import FileUploadZone from '@/components/FileUploadZone.vue';
    import type { ContraAccount, FileItem, SharedData } from '@/types';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';

    const props = defineProps<{
        form: any;
        contraAccounts: ContraAccount[];
    }>();

    const emit = defineEmits<{
        'files-processing': [processing: boolean];
        'files-registered': [];
    }>();

    const MAX_UPLOADS = 5;
    const MAX_SIZE = 9 * 1024 * 1024;

    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const uploadZoneRef = ref<InstanceType<typeof FileUploadZone>>();
    const uploadedFiles = ref<FileItem[]>([]);
    const queryClient = useQueryClient();

    const { t } = useI18n();

    const options = computed(() => {
        return props.contraAccounts.map((account: ContraAccount) => ({
            value: account.ContraAccountHandle_Number,
            label: `${account.ContraAccountHandle_Number} - ${account.name}`,
        }));
    });

    watch(
        () => props.contraAccounts,
        (newOptions, oldOptions) => {
            if (!oldOptions || (newOptions && oldOptions.length !== newOptions.length)) {
                props.form.contra_account_id = newOptions[0].ContraAccountHandle_Number;
            }
        },
        { immediate: true },
    );

    watch(
        () => uploadedFiles.value,
        (newFiles) => {
            if (newFiles.length > 0) {
                props.form.voucher_statement_id = newFiles[0].voucher_statement_id;
            }
        },
    );

    watch(
        () => props.form.contra_account_id,
        (newContraAccountId) => {
            if (newContraAccountId) {
                props.form.voucher_statement_id = null;
                uploadedFiles.value = [];
            }
        },
    );

    const handleFilesProcessing = (processing: boolean) => {
        emit('files-processing', processing);
    };

    const markFilesAsRegistered = () => {
        uploadZoneRef.value?.markFilesAsRegistered();
        emit('files-registered');
    };

    const hasUnregisteredFiles = computed(() => {
        return uploadedFiles.value.some((file) => file.uploaded && !file.registered);
    });

    defineExpose({
        markFilesAsRegistered,
        hasUnregisteredFiles,
    });
</script>

<template>
    <div class="grid gap-3 sm:grid-cols-1">
        <InputRow
            :form="form"
            name="contra_account_id"
            label="vouchers.account_handle"
        >
            <Select
                v-model="form.contra_account_id"
                :options="options"
                :disabled="hasUnregisteredFiles"
            />
        </InputRow>
        <div class="flex flex-col gap-2">
            <Label for="upload_statement">
                {{
                    t('vouchers.upload_statement', {
                        max_uploads: MAX_UPLOADS,
                    })
                }}
            </Label>
            <FileUploadZone
                v-model="uploadedFiles"
                ref="uploadZoneRef"
                :accepts-files="['.pdf']"
                instant-upload
                :route="route('v2.voucher.admin.statement.upload')"
                :additional-fields="{
                    company_id: companyId,
                    contra_account: form.contra_account_id,
                    ...(props.form.voucher_statement_id && {
                        voucher_statement_id: props.form.voucher_statement_id,
                    }),
                }"
                batch-upload
                multiple
                :max-uploads="MAX_UPLOADS"
                :max-size="MAX_SIZE"
                @files-processing="handleFilesProcessing"
                @success="() => queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId] })"
            />
            <!-- @uploaded-files="handleUploadedFiles" -->
        </div>
    </div>
</template>
